import type { fabric } from 'fabric';
import { AssetVersionCommentDto } from '@api/Api';
import { constrainPositionToWindow } from '@pages/MediaViewer/AssetViewer/Canvas/utils/getObjectViewportPosition';
import { hasRenderableCommentAnnotations } from '@pages/MediaViewer/helpers/comment-annotations';

const VIEWPORT_PADDING = 8;
const DOCUMENT_HIGHLIGHT_GAP = 8;

export function resolveFloatRootComment(
  comment: AssetVersionCommentDto | undefined,
  commentsList: AssetVersionCommentDto[]
): AssetVersionCommentDto | undefined {
  if (!comment) return comment;
  if (!comment.topParentId) return comment;
  return (
    commentsList.find((item) => item.id === comment.topParentId) || comment
  );
}

export function commentHasFloatAnchor(
  comment: AssetVersionCommentDto,
  assetType?: string
): boolean {
  if (comment.isCopied) return false;
  if (
    comment.documentHighlight &&
    (comment.documentHighlightRects?.length ?? 0) > 0
  ) {
    return true;
  }
  if (typeof comment.timeCode?.fromSeconds === 'number') {
    return true;
  }
  return hasRenderableCommentAnnotations(comment, assetType);
}

function isNavigableAnnotationComment(
  comment: AssetVersionCommentDto,
  assetType?: string
): boolean {
  if (comment.topParentId) return false;
  return commentHasFloatAnchor(comment, assetType);
}

export function getNavigableAnnotationComments(
  commentsList: AssetVersionCommentDto[],
  assetType?: string
): AssetVersionCommentDto[] {
  const navigable = commentsList.filter((comment) =>
    isNavigableAnnotationComment(comment, assetType)
  );

  if (assetType === 'video' || assetType === 'audio') {
    return navigable.sort((a, b) => {
      const timeA = a.timeCode?.fromSeconds ?? Number.POSITIVE_INFINITY;
      const timeB = b.timeCode?.fromSeconds ?? Number.POSITIVE_INFINITY;
      return timeA - timeB;
    });
  }

  if (
    assetType === 'document' ||
    assetType === 'presentation' ||
    assetType === 'spreadsheet'
  ) {
    // Across all pages: page by page, then by creation within a page
    return navigable.sort(
      (a, b) =>
        (a.documentPage ?? 1) - (b.documentPage ?? 1) ||
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  }

  return navigable.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
}

type DocumentHighlightRect = {
  left: number;
  top: number;
  height: number;
};

export function getDocumentHighlightViewportPosition(
  canvas: fabric.Canvas,
  rects: DocumentHighlightRect[]
): { left: number; top: number } | null {
  if (!rects.length) return null;
  const canvasEl = canvas.getElement();
  if (!canvasEl) return null;
  const canvasRect = canvasEl.getBoundingClientRect();
  const lastRect = rects[rects.length - 1];
  const left = canvasRect.left + (lastRect.left / 100) * canvasRect.width;
  const top =
    canvasRect.top +
    ((lastRect.top + lastRect.height) / 100) * canvasRect.height +
    DOCUMENT_HIGHLIGHT_GAP;
  return constrainPositionToWindow({ left, top }, { canvas });
}

export function getFloatCanvasAnnotations(
  canvas: fabric.Canvas,
  options?: { requireHasAnnotations?: boolean; hasAnnotations?: boolean }
): string | null {
  const json = canvas.toJSON(['assetAngle', 'userEmail', 'userId']) as {
    objects?: fabric.Object[];
  };
  const objects = (json.objects || []).filter(
    (item) => item.type !== 'highlight'
  );
  if (!objects.length) return null;
  if (options?.requireHasAnnotations && !options.hasAnnotations) return null;
  return JSON.stringify(objects);
}

export function clampFloatCommentPosition(
  pos: { left: number; top: number },
  size: { width: number; height: number },
  options?: { canvas?: fabric.Canvas | null }
): { left: number; top: number } {
  const canvasRect = options?.canvas?.getElement()?.getBoundingClientRect();
  const minLeft = canvasRect
    ? canvasRect.left + VIEWPORT_PADDING
    : VIEWPORT_PADDING;
  const minTop = canvasRect
    ? canvasRect.top + VIEWPORT_PADDING
    : VIEWPORT_PADDING;
  const maxLeft = canvasRect
    ? canvasRect.right - size.width - VIEWPORT_PADDING
    : window.innerWidth - size.width - VIEWPORT_PADDING;
  const maxTop = canvasRect
    ? canvasRect.bottom - size.height - VIEWPORT_PADDING
    : window.innerHeight - size.height - VIEWPORT_PADDING;
  const boundedLeft = maxLeft < minLeft ? minLeft : maxLeft;
  const boundedTop = maxTop < minTop ? minTop : maxTop;

  return {
    left: Math.max(minLeft, Math.min(pos.left, boundedLeft)),
    top: Math.max(minTop, Math.min(pos.top, boundedTop))
  };
}

export function getFloatCommentViewportLayout(
  pos: { left: number; top: number },
  naturalHeight: number,
  naturalWidth: number
): { left: number; top: number; maxHeight: number } {
  const maxPossibleHeight = Math.max(
    120,
    window.innerHeight - VIEWPORT_PADDING * 2
  );
  const maxHeight = Math.min(naturalHeight, maxPossibleHeight);
  const nextPos = clampFloatCommentPosition(pos, {
    width: naturalWidth,
    height: maxHeight
  });
  const availableHeight = Math.max(
    120,
    window.innerHeight - nextPos.top - VIEWPORT_PADDING
  );

  return {
    ...nextPos,
    maxHeight: Math.min(maxHeight, availableHeight)
  };
}
